import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Stack, Alert } from "react-bootstrap";
import { patchCaseData } from "../../services/httpClient";
import { Case } from "../../types/case";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import FormCheck from "../ui/FormCheckbox";

interface CaseReassignmentModalProps {
  show: boolean;
  cases: Case[];
  cmUsers: any[];
  deletedUserId?: string | number;
  onComplete: () => void;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const CaseReassignmentModal = ({
  show,
  cases,
  cmUsers,
  deletedUserId,
  onComplete,
  setShowToast,
  setToastText,
  setToastBody,
}: CaseReassignmentModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSelectAllPrimary, setIsSelectAllPrimary] = useState<boolean>(false);
  const [isSelectAllSecondary, setIsSelectAllSecondary] = useState<boolean>(false);
  const [remainingPrimaryCases, setRemainingPrimaryCases] = useState<Case[]>([]);
  const [remainingSecondaryCases, setRemainingSecondaryCases] = useState<Case[]>([]);
  const [selectedPrimary, setSelectedPrimary] = useState<any>(null);
  const [selectedSecondary, setSelectedSecondary] = useState<any>(null);
  const [reassignedPrimaryCases, setReassignedPrimaryCases] = useState<string[]>([]);
  const [reassignedSecondaryCases, setReassignedSecondaryCases] = useState<string[]>([]);
  const [allowAssign, setAllowAssign] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    setRemainingPrimaryCases(cases.filter((c) => (c as any).primaryCM?.user_id === deletedUserId));
    setRemainingSecondaryCases(cases.filter((c) => (c as any).secondaryCM?.user_id === deletedUserId));
  }, [cases, deletedUserId]);

  const updatePrimaryCases = (caseId: string) => {
    if (reassignedPrimaryCases.includes(caseId)) {
      // remove it
      setReassignedPrimaryCases(reassignedPrimaryCases.filter((id) => id !== caseId));
      return;
    }
    setReassignedPrimaryCases((prev) => [...prev, caseId]);
  };

  const updateSecondaryCases = (caseId: string) => {
    if (reassignedSecondaryCases.includes(caseId)) {
      // remove it
      setReassignedSecondaryCases(reassignedSecondaryCases.filter((id) => id !== caseId));
      return;
    }
    setReassignedSecondaryCases((prev) => [...prev, caseId]);
  };

  useEffect(() => {
    const checkAssignAllowed = () => {
      if (
        (reassignedPrimaryCases.length > 0 && selectedPrimary && selectedPrimary !== null) ||
        (reassignedSecondaryCases.length > 0 && selectedSecondary && selectedSecondary !== null)
      ) {
        setAllowAssign(true);
      } else {
        setAllowAssign(false);
      }
    };

    checkAssignAllowed();

    //clear selections if no cases are assigned
    if (reassignedPrimaryCases.length === 0) {
      setSelectedPrimary(null);
    }
    if (reassignedSecondaryCases.length === 0) {
      setSelectedSecondary(null);
    }
  }, [reassignedPrimaryCases, reassignedSecondaryCases, selectedPrimary, selectedSecondary]);

  const onClickSetReassignAll = (type: string) => {
    if (type === "primary") {
      setIsSelectAllPrimary(!isSelectAllPrimary);
      if (!isSelectAllPrimary) {
        // select all
        setReassignedPrimaryCases(remainingPrimaryCases.map((c) => c.id));
        return;
      }
      // deselect all
      setReassignedPrimaryCases([]);
      return;
    } else {
      setIsSelectAllSecondary(!isSelectAllSecondary);
      if (!isSelectAllSecondary) {
        // select all
        setReassignedSecondaryCases(remainingSecondaryCases.map((c) => c.id));
        return;
      }
      // deselect all
      setReassignedSecondaryCases([]);
    }
  };

  const onClickAssign = async () => {
    setIsLoading(true);
    setErrMsg("");

    const successfullyReassignedPrimary: string[] = [];
    if (selectedPrimary?.id) {
      //wait for all and track successes
      for (const caseId of reassignedPrimaryCases) {
        const updateData: any = {
          primaryCM: selectedPrimary?.id,
        };

        try {
          await patchCaseData(updateData, caseId);
          successfullyReassignedPrimary.push(caseId);
        } catch (err: any) {
          console.log(err);
          setErrMsg("Failed to reassign one or more primary cases.");
        }
      }

      //filter out ONLY the successfully reassigned cases
      const remainingPrimary = remainingPrimaryCases.filter(
        (c) => (c as any).primaryCM?.user_id === deletedUserId && !successfullyReassignedPrimary.includes(c.id),
      );

      setRemainingPrimaryCases(remainingPrimary);
    }

    const successfullyReassignedSecondary: string[] = [];
    if (selectedSecondary?.id) {
      //wait for all and track successes
      for (const caseId of reassignedSecondaryCases) {
        const updateData: any = {
          secondaryCM: selectedSecondary?.id,
        };

        try {
          await patchCaseData(updateData, caseId);
          successfullyReassignedSecondary.push(caseId);
        } catch (err: any) {
          console.log(err);
          setErrMsg("Failed to reassign one or more secondary cases.");
        }
      }

      //filter out ONLY the successfully reassigned cases
      const remainingSecondary = remainingSecondaryCases.filter(
        (c) => (c as any).secondaryCM?.user_id === deletedUserId && !successfullyReassignedSecondary.includes(c.id),
      );

      setRemainingSecondaryCases(remainingSecondary);
    }

    //after all updates are done
    setShowToast(true);
    setToastText("Cases Reassigned");
    setToastBody(`${successfullyReassignedPrimary.length + successfullyReassignedSecondary.length} case(s) reassigned successfully`);

    //reset form fields
    setSelectedPrimary("");
    setReassignedPrimaryCases([]);
    setSelectedSecondary("");
    setReassignedSecondaryCases([]);
    setIsLoading(false);
  };

  const onClickCancel = () => {
    setSelectedPrimary("");
    setSelectedSecondary("");
    setReassignedPrimaryCases([]);
    setReassignedSecondaryCases([]);
    setErrMsg("");
    setIsSelectAllPrimary(false);
    setIsSelectAllSecondary(false);
    onComplete();
  };

  const onClickDone = () => {
    setErrMsg("");
    setIsSelectAllPrimary(false);
    setIsSelectAllSecondary(false);
    onComplete();
  };

  if (!show || cases.length === 0) {
    return null;
  }

  return (
    <Modal show={show} onHide={onClickCancel} backdrop={"static"} centered>
      <Modal.Header>
        <Modal.Title>Reassign Cases</Modal.Title>
        {(remainingPrimaryCases.length !== 0 || remainingSecondaryCases.length !== 0) && (
          <div className="text-end ms-auto">
            <Button className="app-primary-color button" onClick={onClickCancel} disabled={isLoading}>
              Skip
            </Button>
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        {errMsg && (
          <div className="errmsg" role="alert">
            {errMsg}
          </div>
        )}
        {remainingPrimaryCases.length > 0 ? (
          <>
            <Stack direction="horizontal" gap={2}>
              <h6>Primary Case Assignments ({remainingPrimaryCases.length})</h6>
              <Alert.Link className="app-tertiary-color-inverse ms-auto mb-2" onClick={() => onClickSetReassignAll("primary")}>
                {isSelectAllPrimary ? "Deselect All" : "Select All"}
              </Alert.Link>
            </Stack>
            <Stack gap={2}>
              <div style={{ maxHeight: "160px", overflowY: "auto", padding: "8px" }}>
                {remainingPrimaryCases.map((myCase) => (
                  <FormCheck
                    key={myCase.id}
                    label={myCase.name}
                    value={reassignedPrimaryCases.includes(myCase.id)}
                    onUpdate={(e: any) => updatePrimaryCases(myCase.id)}
                    disabled={isLoading}
                  />
                ))}
              </div>
              <SelectObjectTypeahead
                label="New Primary Case Manager"
                options={cmUsers}
                value={selectedPrimary}
                onUpdate={(e: any) => setSelectedPrimary(e)}
                disabled={isLoading || reassignedPrimaryCases.length === 0}
              />
            </Stack>
            <hr />
          </>
        ) : (
          <h6 className="app-tertiary-color-inverse mt-0 mb-3">Primary Case Assignments Completed</h6>
        )}
        {remainingSecondaryCases.length > 0 ? (
          <>
            <Stack direction="horizontal" gap={2}>
              <h6>Secondary Case Assignments ({remainingSecondaryCases.length})</h6>
              <Alert.Link className="app-tertiary-color-inverse ms-auto mb-2" onClick={() => onClickSetReassignAll("secondary")}>
                {isSelectAllSecondary ? "Deselect All" : "Select All"}
              </Alert.Link>
            </Stack>
            <Stack gap={2}>
              <div style={{ maxHeight: "160px", overflowY: "auto", padding: "8px" }}>
                {remainingSecondaryCases.map((myCase) => (
                  <FormCheck
                    key={myCase.id}
                    label={myCase.name}
                    value={reassignedSecondaryCases.includes(myCase.id)}
                    onUpdate={(e: any) => updateSecondaryCases(myCase.id)}
                    disabled={isLoading}
                  />
                ))}
              </div>
              <SelectObjectTypeahead
                label="New Secondary Case Manager"
                options={cmUsers}
                value={selectedSecondary}
                onUpdate={(e: any) => setSelectedSecondary(e)}
                disabled={isLoading || reassignedSecondaryCases.length === 0}
              />
            </Stack>
          </>
        ) : (
          <h6 className="app-tertiary-color-inverse mt-3 mb-0">Secondary Case Assignments Completed</h6>
        )}
      </Modal.Body>
      <Modal.Footer>
        {remainingPrimaryCases.length !== 0 || remainingSecondaryCases.length !== 0 ? (
          <Button className="app-tertiary-color button" onClick={onClickAssign} disabled={!allowAssign}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              "Assign Cases"
            )}
          </Button>
        ) : (
          <Button className="app-tertiary-color button mx-auto" onClick={onClickDone}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CaseReassignmentModal;
